import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Tooltip,
  InputAdornment,
  Skeleton,
  Paper,
  Chip,
  Avatar,
  Divider,
  Container,
  FormHelperText
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  Search,
  FilterAlt,
  ViewColumn,
  Person,
  SupervisorAccount,
  AdminPanelSettings,
  Email,
  Badge,
  AccountCircle,
} from '@mui/icons-material';
import { 
  DataGrid, 
  gridClasses,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { alpha, styled } from '@mui/material/styles';

// Color palette que coincide con los KPI cards y la tabla FaqDashboard
const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  purple: '#673ab7',
  error: '#f44336',
  background: '#f7f9fc',
  lightBorder: '#e3f2fd',
  hoverRow: alpha('#2196f3', 0.08)
};

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  backgroundColor: 'white',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 1px 10px 0px rgba(0,0,0,0.025)',
  overflow: 'hidden',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: COLORS.primary,
    color: 'white',
    fontWeight: 'bold',
    borderBottom: 'none',
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiIconButton-root': {
      color: 'white',
    },
  },
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: alpha(COLORS.primary, 0.03),
    '&:hover': {
      backgroundColor: COLORS.hoverRow,
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: COLORS.hoverRow,
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${alpha(COLORS.lightBorder, 0.5)}`,
    padding: '16px 8px',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${COLORS.lightBorder}`,
    backgroundColor: alpha(COLORS.primary, 0.05),
  },
  '& .MuiDataGrid-toolbarContainer': {
    padding: theme.spacing(2),
    backgroundColor: alpha(COLORS.primary, 0.02),
    borderBottom: `1px solid ${alpha(COLORS.lightBorder, 0.5)}`,
    gap: theme.spacing(2),
  },
  '& .MuiTablePagination-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(COLORS.primary, 0.3),
    borderRadius: theme.shape.borderRadius,
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
    backgroundColor: alpha(COLORS.primary, 0.05),
  },
}));

const PageContainer = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 120px)',
  width: '100%',
  padding: theme.spacing(3),
  backgroundColor: COLORS.background,
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#424242',
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  '& svg': {
    marginRight: theme.spacing(1.5),
    color: COLORS.primary,
  }
}));

const DataGridPaper = styled(Paper)(({ theme }) => ({
  height: '100%',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  border: 'none',
  boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 1px 10px 0px rgba(0,0,0,0.025)'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 1px 10px 0px rgba(0,0,0,0.025)',
  padding: '8px 16px',
  fontWeight: 500,
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius,
  },
  '& .MuiDialogTitle-root': {
    backgroundColor: alpha(COLORS.primary, 0.05),
    padding: theme.spacing(2),
    fontWeight: 600,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
    backgroundColor: alpha(COLORS.background, 0.5),
  },
}));

const RoleChip = styled(Chip)(({ role, theme }) => {
  let color = COLORS.primary;
  let bgColor = alpha(COLORS.primary, 0.1);

  if (role === 'Administrador') {
    color = COLORS.purple;
    bgColor = alpha(COLORS.purple, 0.1);
  } else if (role === 'Supervisor') {
    color = COLORS.warning;
    bgColor = alpha(COLORS.warning, 0.1);
  }

  return {
    backgroundColor: bgColor,
    color: color,
    fontWeight: 500,
    border: `1px solid ${alpha(color, 0.3)}`,
    '& .MuiChip-avatar': {
      backgroundColor: color,
      color: 'white',
    },
  };
});

function getRoleIcon(role) {
  switch (role) {
    case 'Administrador':
      return <AdminPanelSettings />;
    case 'Supervisor':
      return <SupervisorAccount />;
    default:
      return <Person />;
  }
}

function TableSkeleton() {
  return (
    <DataGridPaper sx={{ p: 2 }}>
      {/* Header Skeleton */}
      <Box sx={{ display: 'flex', mb: 1, pb: 2, borderBottom: `1px solid ${alpha(COLORS.lightBorder, 0.5)}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1.5 }} />
          <Skeleton variant="text" width={230} height={40} />
        </Box>
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="rounded" width={150} height={40} sx={{ borderRadius: 1 }} />
      </Box>
      
      {/* Table Header */}
      <Box sx={{ 
        display: 'flex', 
        py: 2,
        backgroundColor: alpha(COLORS.primary, 0.05),
        px: 2,
        borderRadius: 1,
        mb: 1
      }}>
        <Skeleton variant="text" width={200} height={30} sx={{ mr: 2 }} />
        <Skeleton variant="text" width={150} height={30} sx={{ mr: 2 }} />
        <Skeleton variant="text" width={150} height={30} sx={{ mr: 2 }} />
        <Skeleton variant="text" width={150} height={30} sx={{ mr: 2 }} />
        <Skeleton variant="text" width={120} height={30} sx={{ mr: 2 }} />
        <Skeleton variant="text" width={100} height={30} />
      </Box>
      
      {/* Table Rows */}
      {[...Array(5)].map((_, index) => (
        <Box 
          key={index} 
          sx={{ 
            display: 'flex', 
            py: 2,
            px: 2, 
            borderBottom: `1px solid ${alpha(COLORS.lightBorder, 0.5)}`,
            backgroundColor: index % 2 === 0 ? alpha(COLORS.primary, 0.02) : 'white'
          }}
        >
          <Skeleton variant="text" width={200} height={30} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={150} height={30} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={150} height={30} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={150} height={30} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={120} height={30} sx={{ mr: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="circular" width={30} height={30} sx={{ mr: 1 }} />
            <Skeleton variant="circular" width={30} height={30} />
          </Box>
        </Box>
      ))}
      
      {/* Footer pagination */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        pt: 2, 
        px: 2,
        backgroundColor: alpha(COLORS.primary, 0.02),
        borderTop: `1px solid ${alpha(COLORS.lightBorder, 0.5)}`,
      }}>
        <Skeleton variant="text" width={100} height={30} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Skeleton variant="text" width={80} height={30} sx={{ mr: 2 }} />
          <Skeleton variant="rounded" width={80} height={30} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    </DataGridPaper>
  );
}

function CustomToolbar({ onNewUser, disabled }) {
  return (
    <GridToolbarContainer>
      <GridToolbarQuickFilter
        sx={{
          '& .MuiTextField-root': {
            width: 200,
            transition: 'width 0.2s',
            '&:focus-within': {
              width: 250,
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.primary,
              },
            },
          },
        }}
        placeholder="Buscar usuario..."
        quickFilterParser={(searchInput) => 
          searchInput
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value !== '')
        }
      />
      <Box sx={{ flex: 1 }} />
      <Tooltip title="Agregar nuevo usuario">
        <StyledButton
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={onNewUser}
          disabled={disabled}
        >
          Nuevo Usuario
        </StyledButton>
      </Tooltip>
    </GridToolbarContainer>
  );
}

function TenantUsers(props) {
  const { profile, tenantUsers, loading, errorMessage, getTenantUsers, createTenantUser, updateTenantUser, deleteTenantUser } = props;
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'Agente',
    tenant_id: '',
  });
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    if (profile && profile.tenant_id) {
      getTenantUsers(profile.tenant_id);
    }
  }, [profile]);

  useEffect(() => {
    // Validate form whenever formData changes
    validateForm();
  }, [formData, isEdit]);

  const handleClickOpen = (isEditMode = false, user = null) => {
    setIsEdit(isEditMode);
    setSelectedUser(user);
    
    if (isEditMode && user) {
      setFormData({
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        password: '',
        role: user.role || 'Agente',
        tenant_id: profile.tenant_id,
      });
    } else {
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'Agente',
        tenant_id: profile.tenant_id,
      });
    }
    
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    setSelectedUser(null);
    setErrors({});
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    
    // Clear error when field is changed
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate required fields
    if (!formData.email) newErrors.email = 'El email es requerido';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';
    
    if (!formData.first_name) newErrors.first_name = 'El nombre es requerido';
    if (!formData.last_name) newErrors.last_name = 'El apellido es requerido';
    
    // Validate password only if creating a new user or changing password for existing user
    if (!isEdit || (isEdit && formData.password)) {
      // Password is required for new users
      if (!isEdit && !formData.password) {
        newErrors.password = 'La contraseña es requerida';
      } else if (formData.password) {
        // Password complexity validation
        const hasMinLength = formData.password.length >= 8;
        const hasUpperCase = /[A-Z]/.test(formData.password);
        const hasNumber = /[0-9]/.test(formData.password);
        const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password);
        
        if (!hasMinLength || !hasUpperCase || !hasNumber || !hasSymbol) {
          newErrors.password = 'La contraseña debe tener al menos 8 caracteres, una mayúscula, un número y un símbolo';
        }
      }
    }
    
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    setIsFormValid(isValid);
    return isValid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      if (isEdit && selectedUser) {
        updateTenantUser(selectedUser.user_id, formData);
      } else {
        createTenantUser(formData);
      }
      handleClose();
    }
  };

  const handleDelete = (user) => {
    if (window.confirm('¿Está seguro que desea eliminar este usuario?')) {
      deleteTenantUser(user.user_id, profile.tenant_id);
    }
  };

  const columns = [
    { 
      field: 'email', 
      headerName: 'Email', 
      flex: 1, 
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Email fontSize="small" sx={{ color: COLORS.primary, opacity: 0.7, mr: 1 }} />
          <Typography>{params.value}</Typography>
        </Box>
      )
    },
    { 
      field: 'first_name', 
      headerName: 'Nombre', 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => (
        <Typography>{params.value}</Typography>
      )
    },
    { 
      field: 'last_name', 
      headerName: 'Apellido', 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => (
        <Typography>{params.value}</Typography>
      )
    },
    { 
      field: 'role', 
      headerName: 'Rol', 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => {
        const role = params.value || 'Agente';
        return (
          <RoleChip
            role={role}
            avatar={<Avatar>{role[0]}</Avatar>}
            label={role}
            variant="outlined"
            size="small"
            icon={getRoleIcon(role)}
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 0.7,
      minWidth: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex' }}>
          <Tooltip title="Editar usuario">
            <IconButton
              sx={{ 
                color: COLORS.primary,
                '&:hover': { backgroundColor: alpha(COLORS.primary, 0.1) },
                mr: 0.5
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleClickOpen(true, params.row);
              }}
              size="small"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar usuario">
            <IconButton
              sx={{ 
                color: COLORS.error,
                '&:hover': { backgroundColor: alpha(COLORS.error, 0.1) },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.row);
              }}
              size="small"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = tenantUsers?.map(user => ({
    id: user.user_id,
    ...user
  })) || [];

  const CustomToolbarWithProps = () => (
    <CustomToolbar
      onNewUser={() => handleClickOpen(false)}
      disabled={!profile || !profile.tenant_id}
    />
  );

  return (
    <PageContainer>
      <PageTitle variant="h5">
        <Person fontSize="medium" />
        Gestión de Usuarios
      </PageTitle>

      {loading ? (
        <TableSkeleton />
      ) : errorMessage ? (
        <Box sx={{ p: 3, backgroundColor: '#ffebee', borderRadius: 1 }}>
          <Typography color="error">{errorMessage}</Typography>
        </Box>
      ) : (
        <Container maxWidth="xl">
        <DataGridPaper>

          <StripedDataGrid
            rows={rows}
            columns={columns}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            initialState={{
              pagination: { paginationModel: { pageSize: 5 } },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableColumnResize
            density="comfortable"
            slots={{
              toolbar: CustomToolbarWithProps,
            }}
            components={{
              NoRowsOverlay: () => (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  p: 3,
                  color: 'text.secondary' 
                }}>
                  <AccountCircle sx={{ fontSize: 40, color: alpha(COLORS.primary, 0.3), mb: 1 }} />
                  <Typography>No hay usuarios disponibles</Typography>
                </Box>
              )
            }}
          />
        </DataGridPaper>
        </Container>
      )}

      <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEdit ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            name="first_name"
            label="Nombre"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.first_name}
            onChange={handleChange}
            error={!!errors.first_name}
            helperText={errors.first_name}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            name="last_name"
            label="Apellido"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.last_name}
            onChange={handleChange}
            error={!!errors.last_name}
            helperText={errors.last_name}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            name="password"
            label="Contraseña"
            type="password"
            fullWidth
            variant="outlined"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password || (isEdit ? "Deje este campo en blanco si no desea cambiar la contraseña" : "Mínimo 8 caracteres, incluir mayúscula, número y símbolo")}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            required={!isEdit}
          />
          <FormControl 
            fullWidth 
            margin="dense"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: COLORS.primary,
                },
              }
            }}
          >
            <InputLabel id="role-label">Rol</InputLabel>
            <Select
              labelId="role-label"
              name="role"
              value={formData.role}
              label="Rol"
              onChange={handleChange}
              startAdornment={
                <InputAdornment position="start">
                  {getRoleIcon(formData.role)}
                </InputAdornment>
              }
            >
              <MenuItem value="Agente">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Person sx={{ mr: 1, color: COLORS.primary }} />
                  Agente
                </Box>
              </MenuItem>
              <MenuItem value="Supervisor">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SupervisorAccount sx={{ mr: 1, color: COLORS.warning }} />
                  Supervisor
                </Box>
              </MenuItem>
              <MenuItem value="Administrador">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AdminPanelSettings sx={{ mr: 1, color: COLORS.purple }} />
                  Administrador
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClose}
            sx={{ 
              color: alpha('#424242', 0.8),
              '&:hover': {
                backgroundColor: alpha('#424242', 0.05),
              }
            }}
          >
            Cancelar
          </Button>
          <StyledButton 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={!isFormValid}
          >
            {isEdit ? 'Actualizar' : 'Crear'}
          </StyledButton>
        </DialogActions>
      </StyledDialog>
    </PageContainer>
  );
}

export default TenantUsers; 